import { clamp } from 'lodash';
import { MutableRefObject } from 'react';

interface SliderProps {
    min: number;
    max: number;
    checkpoint?: number;
}

/**
 * Converts a position along the rail (between 0 and 1) into the visible value for the slider (between min and max)
 */
export const getSliderValueFromRailPosition = (railPosition: number, sliderProps: SliderProps): number => {
    const { min, max, checkpoint } = sliderProps;

    let value;
    if (!checkpoint) {
        value = railPosition * (max - min) + min;
    } else if (railPosition <= 0.5) {
        value = (railPosition / 0.5) * (checkpoint - min) + min;
    } else {
        value = ((railPosition - 0.5) / 0.5) * (max - checkpoint) + checkpoint;
    }

    return clamp(value, min, max);
};

/**
 * Converts the value for the slider (between min and max) into a position along the rail (between 0 and 1)
 */
export const getSliderRailPositionFromValue = (value: number, sliderProps: SliderProps): number => {
    const { min, max, checkpoint } = sliderProps;

    let railPosition;
    if (!checkpoint) {
        railPosition = (value - min) / (max - min);
    } else if (value <= checkpoint) {
        railPosition = ((value - min) / (checkpoint - min)) * 0.5;
    } else {
        railPosition = 0.5 + ((value - checkpoint) / (max - checkpoint)) * 0.5;
    }

    return clamp(railPosition, 0, 1);
};

/**
 * Based on previous rail position and current rail position, determine whether the slider handle should snap to
 * the checkpoint.
 */
export const shouldSnapToCheckpoint = (
    startingRailPosition: MutableRefObject<number | null>,
    prevRailPositionRef: MutableRefObject<number | null>,
    railPosition: number,
    props: SliderProps,
): boolean => {
    if (props.checkpoint === undefined) return false;
    if (prevRailPositionRef.current === null || startingRailPosition.current === null) return false;

    // If user start dragging the slider handle when their rail position is further away from the center, and then
    // drag towards the center of the slider rail, make sure to snap to the checkpoint and maintain that position (to
    // create a sticky feeling) until they drag the slider handle far enough.
    if (startingRailPosition.current < 0.375 || startingRailPosition.current > 0.625) {
        return railPosition >= 0.375 && railPosition <= 0.625;
    }

    // If the user start dragging the slider handle from the middle section, then:
    // - When dragging handle further away from the checkpoint, do not make the handle "stick" to checkpoint
    // - When dragging handle towards the checkpoint, make the handle "stick" to the checkpoint
    const delta = railPosition - prevRailPositionRef.current;
    return (
        (delta > 0 && railPosition >= 0.375 && railPosition < 0.5) ||
        (delta < 0 && railPosition > 0.5 && railPosition <= 0.625)
    );
};

/**
 * Get position along the rail (between 0 and 1) based on an event's x position
 */
export const getRailPositionFromXPos = (eventXPos: number, sliderHandleRect: DOMRect): number => {
    return clamp((eventXPos - sliderHandleRect.x) / sliderHandleRect.width, 0, 1);
};
